import React, { useState } from 'react';
import { BsFileEarmarkArrowUp, BsTrash } from 'react-icons/bs';
import { FaSyncAlt } from 'react-icons/fa';

import CreateFile from './CreateFile';
import BBITable from '../../../helpers/bBITable/BBITable';
import PostForm from '../../../api/internal/PostForm';
import PostBody from '../../../api/internal/PostBody';
import Delete from '../../../api/internal/Delete';
import DateTimeUTCFormatter from '../../../helpers/inputs/DateTimeUTCFormatter';
import classes from '../../../assets/Global.module.css';
import classesFiles from './File.module.css';
import Create from '../../../helpers/error/Create';
import Error from '../../../helpers/error/Error';
import EmailAllFiles from './EmailAllFiles';

export type FileCategory =
  | 'driver'
  | 'asset'
  | 'customer'
  | 'vendor'
  | 'load'
  | 'rental'
  | 'invoice'
  | 'bill'
  | 'maintenance-log'
  | 'general-documents'
  | 'comchek';

function Files({
  data,
  fileCategory,
  categoryId,
  RefreshPage,
  GetLoad,
}: {
  data: Array<BlobFile>;
  categoryId: number;
  fileCategory: FileCategory;
  RefreshPage: Function;
  GetLoad?: Function;
}) {
  const PULL_PODS_TEXT = 'Pull PODs from Samsara';
  const [isCreateFileOpen, setIsCreateFileOpen] = useState<boolean>(false);
  const [isPullingPods, setIsPullingPods] = useState<boolean>(false);
  const [pullPodsText, setPullPodsText] = useState<string>(PULL_PODS_TEXT);
  const [isEmailFilesOpen, setIsEmailFilesOpen] = useState<boolean>(false);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);

  const newData = data.map(file => ({
    ...file,
    name: (
      <a href={file.uri} target="_blank" rel="noreferrer">
        {file.name}
      </a>
    ),
    type: file.metadata.documentType,
    date: DateTimeUTCFormatter(file.createdOn),
    updatedBy: file.metadata.uploadedBy,
    delete: (
      <BsTrash
        className={`${classes.clickable} ${classes.center}`}
        onClick={() => {
          DeleteFile(file.name);
        }}
      />
    ),
  }));

  const columnConfig = [
    {
      key: '1',
      attribute: 'name',
      header: 'Name',
    },
    {
      key: '2',
      attribute: 'type',
      header: 'Type',
    },
    {
      key: '3',
      attribute: 'date',
      header: 'Date',
    },
    {
      key: '4',
      attribute: 'updatedBy',
      header: 'Uploaded By',
    },
    {
      key: '5',
      attribute: 'delete',
      width: '3ch',
    },
  ];

  function UploadFiles(e) {
    const formData = new FormData(e.target);
    PostForm(`/File/UploadFiles/${fileCategory}-${categoryId}`, formData).then(
      response => {
        if (response) {
          RefreshPage('Files', ['Audit', 'Maintenance Logs']);
          if (response.status === 207)
            Create(
              <Error
                customMessage={response.data ?? 'Some files failed to upload.'}
                messageCharacterLimit={400}
              />,
            );
        }
      },
    );
  }

  function PullSamsaraPods() {
    setIsPullingPods(true);
    setPullPodsText('Pulling PODs...');
    PostBody(`/Samsara/SyncLoadPods/${categoryId}`, {}).then(response => {
      setIsPullingPods(false);
      if (!response) {
        setPullPodsText(PULL_PODS_TEXT);
        return;
      }

      const { ingested, alreadyPresent, unmatched, failed } = response.data;
      if (ingested > 0) {
        setPullPodsText(`Pulled ${ingested} POD${ingested > 1 ? 's' : ''}`);
        // POD upload can move the load to Accounting In Process, so the status and summary need refreshing too
        RefreshPage('Files', ['Audit', 'Summary']);
        GetLoad?.();
      } else if (failed > 0) {
        setPullPodsText('Samsara PDF not ready, try again');
      } else if (unmatched > 0) {
        setPullPodsText('POD found, but its driver is not on this load');
      } else if (alreadyPresent > 0) {
        setPullPodsText('Samsara PODs already pulled');
      } else {
        setPullPodsText('No PODs found in Samsara');
      }
    });
  }

  function DeleteFile(fileName: string) {
    const encodedFileName = encodeURIComponent(fileName);
    Delete(
      `/File/DeleteFile/${fileCategory}-${categoryId}/${encodedFileName}`,
    ).then(() => {
      RefreshPage('Files', ['Audit', 'Maintenance Logs']);
    });
  }
  return (
    <>
      <div className={classesFiles.btnHolder}>
        <button
          type="button"
          onClick={() => {
            setIsCreateFileOpen(!isCreateFileOpen);
            setIsEmailFilesOpen(false);
          }}
        >
          <BsFileEarmarkArrowUp />
          <span>Upload File</span>
        </button>
        {fileCategory === 'asset' ? (
          <button
            type="button"
            onClick={() => {
              setIsEmailFilesOpen(!isEmailFilesOpen);
              setIsCreateFileOpen(false);
            }}
          >
            <BsFileEarmarkArrowUp />
            <span>Email All Files</span>
          </button>
        ) : null}
        {fileCategory === 'load' ? (
          <button
            type="button"
            onClick={PullSamsaraPods}
            disabled={isPullingPods}
          >
            <FaSyncAlt />
            <span>{pullPodsText}</span>
          </button>
        ) : null}
      </div>
      <CreateFile
        UploadFile={UploadFiles}
        uploadFileListType={fileCategory}
        isOpen={isCreateFileOpen}
        setIsOpen={setIsCreateFileOpen}
      />
      {fileCategory === 'asset' && isEmailFilesOpen ? (
        <EmailAllFiles
          fileCategory={fileCategory}
          categoryId={categoryId}
          emailRecipients={emailRecipients}
          setEmailRecipients={setEmailRecipients}
          isOpen={isEmailFilesOpen}
          setIsOpen={setIsEmailFilesOpen}
        />
      ) : null}
      {data.length !== 0 ? (
        <BBITable data={newData} columns={columnConfig} />
      ) : (
        <div>No files have been uploaded.</div>
      )}
    </>
  );
}
export default Files;
